import type { useTranslations } from "use-intl";
import {
  array,
  date,
  literal,
  nativeEnum,
  number,
  object,
  string,
  union,
  null as zodNull,
  ZodIssueCode,
  coerce,
} from "zod";

import { MIN_ADULT_AGE } from "@/app/lib/constants";
import { TravelPurpose } from "@/app/lib/types/enums";
import { citySchema } from "@/app/lib/utils/locations";

export const convertArabicNumberToEnglish = (n: string) => {
  const ar = "٠١٢٣٤٥٦٧٨٩".split("");
  const en = "0123456789".split("");

  const englishNumberStr = n
    .replace(/[٠١٢٣٤٥٦٧٨٩]/g, (x) => en[ar.indexOf(x)])
    .replace(/[^\d]/g, "");

  return Number(englishNumberStr);
};

export const getDatesValidationSchema = (
  t: ReturnType<typeof useTranslations>,
) =>
  object({
    checkIn: date({ required_error: t("hotels.validation.dates.checkIn") }),
    checkOut: date().refine((v) => v !== null),
  }).superRefine((values, ctx) => {
    const { checkIn, checkOut } = values;

    if (checkOut < checkIn) {
      ctx.addIssue({
        code: "custom",
        path: ["checkOut"],
        message: t("hotels.validation.dates.min"),
      });
    }
  });

export const getRoomsValidationSchema = (
  t: ReturnType<typeof useTranslations>,
) =>
  array(
    object({
      adults: number().min(1),
      children: number().min(0),
      childrenAges: array(
        union([
          number()
            .int()
            .min(0)
            .max(MIN_ADULT_AGE - 1),
          string()
            .transform((v) => {
              if (v.trim() === "") return v;
              if (isNaN(Number(v))) {
                return convertArabicNumberToEnglish(v).toString();
              }
              return v;
            })
            .pipe(string()),
        ]),
      ),
    }),
  )
    .min(1, t("hotels.validation.roomsMin"))
    .max(5, t("hotels.validation.roomsMax"));

export const getValidationSchema = (t: ReturnType<typeof useTranslations>) =>
  object({
    destination: union([
      object({
        id: string().min(1).regex(/^\d+$/),
        name: string().min(1, t("hotels.validation.destination")),
        code: string().optional(),
        countryName: string(),
        countryCode: string().optional(),
        type: literal("CITY"),
      }),
      zodNull(),
    ]).superRefine((v, ctx) => {
      if (v === null) {
        ctx.addIssue({
          code: ZodIssueCode.custom,
          message: t("hotels.validation.destination"),
        });
      }
    }),

    dates: getDatesValidationSchema(t),
    rooms: getRoomsValidationSchema(t),
    travelPurpose: nativeEnum(TravelPurpose),
  });

export type HotelFormValuesSchema = ReturnType<typeof getValidationSchema>;

export const getHotelFormValuesSchema = (
  t: ReturnType<typeof useTranslations>,
) =>
  object({
    destination: citySchema,
    dates: object({
      checkIn: coerce.date(),
      checkOut: coerce.date(),
    }),
    rooms: getRoomsValidationSchema(t),
    travelPurpose: nativeEnum(TravelPurpose),
  });
